import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { elliottTokens as t } from '../tokens';

const SEP = '\n\n';

type StreamingTextProps = {
  paragraphs: string[];
  sx?: SxProps<Theme>;
  charsPerTick?: number;
  tickMs?: number;
  enabled?: boolean; // when false, renders the full text immediately (no animation)
  // Hold the typewriter at zero until this flips false — used by voice mode to
  // sync the caption with the moment Elliott's audio actually starts.
  paused?: boolean;
  // Hold the typewriter where it is, mid-message, and carry on from the same
  // character when this flips back. Unlike `paused`, it never restarts.
  frozen?: boolean;
  // Once the copy is fully typed out, mark this passage in it: the sentence
  // Elliott is speaking, so the learner can follow along (and on a replay).
  highlight?: string;
  onDone?: () => void;
};

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Where `passage` sits in `full`, tolerant of whitespace differences: the
// speech engines normalise whitespace when they split a turn into sentences.
const findPassage = (full: string, passage?: string): [number, number] | null => {
  const words = (passage || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  const match = new RegExp(words.map(escapeRegExp).join('\\s+')).exec(full);
  return match ? [match.index, match.index + match[0].length] : null;
};

// Types Elliott's copy out live, paragraph by paragraph, with a blinking caret while streaming.
const StreamingText: React.FC<StreamingTextProps> = ({
  paragraphs,
  sx,
  charsPerTick = 3,
  tickMs = 16,
  enabled = true,
  paused = false,
  frozen = false,
  highlight,
  onDone
}) => {
  const full = paragraphs.join(SEP);
  const [count, setCount] = useState(enabled ? 0 : full.length);
  const doneRef = useRef(false);
  const frozenRef = useRef(frozen);
  frozenRef.current = frozen;

  useEffect(() => {
    if (!enabled) {
      // Instant mode (e.g. rehydrating a resumed lesson): render everything and
      // still fire onDone so reveal gates that depend on it open immediately.
      setCount(full.length);
      if (!doneRef.current) {
        doneRef.current = true;
        onDone?.();
      }
      return;
    }
    if (paused) return; // waiting for the voice — caret blinks, no text yet
    doneRef.current = false;
    setCount(0);
    let i = 0;
    const id = setInterval(() => {
      if (frozenRef.current) return; // the learner paused Elliott mid-sentence
      i = Math.min(i + charsPerTick, full.length);
      setCount(i);
      if (i >= full.length) {
        clearInterval(id);
        if (!doneRef.current) {
          doneRef.current = true;
          onDone?.();
        }
      }
    }, tickMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full, enabled, paused]);

  const parts = full.slice(0, count).split(SEP);
  const streaming = count < full.length;
  const marked = streaming ? null : findPassage(full, highlight);

  // Paragraph `idx` starts this far into `full` (each separator is SEP long).
  let offset = 0;

  return (
    <>
      {parts.map((p, idx) => {
        const start = offset;
        offset += p.length + SEP.length;
        const from = marked ? Math.max(marked[0], start) - start : -1;
        const to = marked ? Math.min(marked[1], start + p.length) - start : -1;
        const hasMark = marked !== null && from < to;
        return (
          <Typography key={idx} sx={sx}>
            {hasMark ? (
              <>
                {p.slice(0, from)}
                <Box
                  component="mark"
                  sx={{
                    color: 'inherit',
                    bgcolor: 'rgba(247,148,32,0.16)',
                    borderRadius: '3px',
                    boxShadow: '0 0 0 3px rgba(247,148,32,0.16)',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  {p.slice(from, to)}
                </Box>
                {p.slice(to)}
              </>
            ) : (
              p
            )}
            {streaming && idx === parts.length - 1 && (
              <Box
                component="span"
                sx={{
                  ml: '2px',
                  color: t.brandLight,
                  animation: 'elliottCaret 1s steps(1) infinite',
                  '@keyframes elliottCaret': {
                    '0%, 50%': { opacity: 1 },
                    '50.01%, 100%': { opacity: 0 }
                  }
                }}
              >
                ▍
              </Box>
            )}
          </Typography>
        );
      })}
    </>
  );
};

export default StreamingText;
